import React, { useState, useRef, useEffect, useCallback } from 'react';

import styled from 'styled-components';

import ReactQuill, { Quill } from 'react-quill';

import 'react-quill/dist/quill.snow.css';

import axios from 'axios';

import { FiX, FiEdit2, FiLock, FiEdit } from 'react-icons/fi';

import { MdOutlineRotateLeft } from 'react-icons/md';

import { ToastContainer, toast } from 'react-toastify';

import 'react-toastify/dist/ReactToastify.css';

/* ═══════════════════════════════════════════════════════
   BADGE DIMENSIONS
═══════════════════════════════════════════════════════ */
const BADGE_NATIVE_W = 265;
const BADGE_NATIVE_H = 378;

const DIVIDER_TEXT = '───────────────────────────────────';

/* ═══════════════════════════════════════════════════════
   DEFAULT BACK-PASS CONTENT
═══════════════════════════════════════════════════════ */
const DEFAULT_BACK_CONTENT = `<h3 style="text-align: center;">VIP Pass - Instructions</h3>
<p><strong>1.</strong> This pass must be worn visibly at all times.</p>
<p><strong>2.</strong> Follow all safety guidelines from security staff.</p>
<p><strong>3.</strong> Photography/videography prohibited unless authorized.</p>
<p><strong>4.</strong> Return this pass at security gate before exiting.</p>
<p><strong>5.</strong> Carry valid identity proof during your visit.</p>
<p><strong>6.</strong> Report suspicious activity to security immediately.</p>
<hr class="divider" data-divider="true">
<p><strong>Emergency Contact</strong></p>
<p>Security Helpdesk: <strong>+91-9686194862</strong><br/>Occupational Health Centre: <strong>+91-9731802911</strong><br/>Fire Safety: <strong>101</strong></p>
<hr class="divider" data-divider="true">
<p><strong>Disclaimer:</strong> The organization is not responsible for loss/damage to personal belongings. Please comply with all rules to ensure your safety.</p>`;

/* ═══════════════════════════════════════════════════════
   HTML BUILDERS
═══════════════════════════════════════════════════════ */

/**
 * Builds a STANDALONE back-pass HTML document used only for the PREVIEW iframe.
 * rotate=false for preview (we want to see it right-side up in the editor).
 */
const buildBackPassPreviewHtml = (quillHtml) => {
  let processedHtml = quillHtml || '';

  processedHtml = processedHtml.replace(
    new RegExp(`<p[^>]*>\\s*(?:<[^>]+>)*\\s*${DIVIDER_TEXT}\\s*(?:<[^>]+>)*\\s*</p>`, 'g'),
    '<hr class="divider" data-divider="true">'
  );
  processedHtml = processedHtml.replace(
    new RegExp(DIVIDER_TEXT, 'g'),
    '<hr class="divider" data-divider="true">'
  );

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { width: 265px; height: 378px; overflow: hidden; background: #fff; }
  body { font-family: "Segoe UI", sans-serif; font-size: 9px; color: #000; }
  .badge-container {
    border: 2px solid #000;
    width: 265px;
    height: 378px;
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .content-section { flex: 1; font-size: 9px; line-height: 1.4; overflow: hidden; min-height: 0; }
  .content-section h1, .content-section h2, .content-section h3 {
    font-size: 11px; margin: 4px 0 8px 0; font-weight: bold; text-align: center;
  }
  .content-section p { margin: 3px 0; }
  hr.divider, hr[data-divider="true"] {
    border: none !important; border-top: 1px solid #000 !important;
    margin: 8px 0 !important; height: 1px !important;
    overflow: hidden !important; display: block !important;
  }
  .ql-align-center  { text-align: center  !important; }
  .ql-align-right   { text-align: right   !important; }
  .ql-align-justify { text-align: justify !important; }
</style>
</head>
<body>
<div class="badge-container">
<div style="height:16px;text-align:center;margin-bottom:6px;">#Logo#</div>
<div class="content-section">${processedHtml}</div>
</div>
</body>
</html>`;
};

/**
 * Builds the back-pass HTML fragment that is injected into the COMBINED template
 * (the one stored in htmlFront after <!-- HTML Back: -->).
 *
 * This must exactly replicate the structure from the original DB template (Document 2):
 *  - Same outer <html>/<body>/<style> skeleton
 *  - .badge-container with .back-rotate for the 180° flip
 *  - Content injected inside .badge-container (after logo placeholder)
 *
 * IMPORTANT: We do NOT generate a standalone preview doc here.
 * We rebuild the exact HTML fragment the print engine expects.
 */
const buildBackPassForStorage_Combined = (quillHtml) => {
  let processedHtml = quillHtml || '';

  // Convert text divider placeholders back to real <hr> tags
  processedHtml = processedHtml.replace(
    new RegExp(`<p[^>]*>\\s*(?:<[^>]+>)*\\s*${DIVIDER_TEXT}\\s*(?:<[^>]+>)*\\s*</p>`, 'g'),
    '<hr class="divider" data-divider="true" style="border:none;border-top:1px solid #000;margin:8px 0;">'
  );
  processedHtml = processedHtml.replace(
    new RegExp(DIVIDER_TEXT, 'g'),
    '<hr class="divider" data-divider="true" style="border:none;border-top:1px solid #000;margin:8px 0;">'
  );

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<style>
    body {
      margin: 0;
      padding: 0;
      font-family: "Segoe UI", sans-serif;
      font-size: 9.5px;
      height: 29.7cm;
      width: 21cm;
      display: flex;
      justify-content: flex-start;
      align-items: flex-start;
    }
    .page {
      display: flex;
      gap: 1mm;
      margin-top: 0;
      margin-left: 0;
    }
    .back-rotate {
      transform: rotate(180deg);
      transform-origin: center center;
    }
    .badge-container {
      border: 2px solid #000;
      width: 7cm;
      height: 10cm;
      padding: 8px;
      position: relative;
      box-sizing: border-box;
      font-family: "Segoe UI", sans-serif;
      font-size: 9px;
    }
    .badge-container h1,
    .badge-container h2,
    .badge-container h3 {
      font-size: 11px; margin: 4px 0 8px 0; font-weight: bold; text-align: center;
    }
    .badge-container p { margin: 3px 0; }
    hr.divider, hr[data-divider="true"] {
      border: none; border-top: 1px solid #000; margin: 8px 0; height: 1px; display: block;
    }
    .ql-align-center  { text-align: center  !important; }
    .ql-align-right   { text-align: right   !important; }
    .ql-align-justify { text-align: justify !important; }
</style>
</head>
<body>
<div class="page">
<div class="badge-container back-rotate">
<div style="height:30px;text-align:center;margin-bottom:6px;">#Logo1#</div>
${processedHtml}
</div>
</div>
</body>
</html>`;
};

/**
 * Builds the back-pass HTML for the SEPARATE storage mode (htmlBack field).
 * Same structure as combined but stored independently.
 * rotate=true since the print engine renders htmlBack as a separate page.
 */
const buildBackPassForStorage_Separate = (quillHtml) => {
  let processedHtml = quillHtml || '';

  processedHtml = processedHtml.replace(
    new RegExp(`<p[^>]*>\\s*(?:<[^>]+>)*\\s*${DIVIDER_TEXT}\\s*(?:<[^>]+>)*\\s*</p>`, 'g'),
    '<hr class="divider" data-divider="true">'
  );
  processedHtml = processedHtml.replace(
    new RegExp(DIVIDER_TEXT, 'g'),
    '<hr class="divider" data-divider="true">'
  );

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { width: 265px; height: 378px; overflow: hidden; background: #fff; }
  body { font-family: "Segoe UI", sans-serif; font-size: 9px; color: #000; }
  .badge-container {
    border: 2px solid #000;
    width: 265px;
    height: 378px;
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    transform: rotate(180deg);
    transform-origin: center center;
  }
  .logo-section { flex-shrink: 0; text-align: center; margin-bottom: 8px; }
  .content-section { flex: 1; font-size: 9px; line-height: 1.4; overflow: hidden; min-height: 0; }
  .content-section h1, .content-section h2, .content-section h3 {
    font-size: 11px; margin: 4px 0 8px 0; font-weight: bold; text-align: center;
  }
  .content-section p { margin: 3px 0; }
  hr.divider, hr[data-divider="true"] {
    border: none !important; border-top: 1px solid #000 !important;
    margin: 8px 0 !important; height: 1px !important;
    overflow: hidden !important; display: block !important;
  }
  .ql-align-center  { text-align: center  !important; }
  .ql-align-right   { text-align: right   !important; }
  .ql-align-justify { text-align: justify !important; }
</style>
</head>
<body>
<div class="badge-container">
<div class="logo-section"></div>
<div class="content-section">${processedHtml}</div>
</div>
</body>
</html>`;
};

const FRONT_PASS_HTML = `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { width: 267px; height: 380px; overflow: hidden; background: #fff; }
  body { font-family: "Segoe UI", sans-serif; color: #000; }
  .badge-container {
    border: 2px solid #000;
    width: 265px;
    height: 378px;
    padding: 10px 12px;
    position: relative;
    overflow: hidden;
  }
  .logo-section { text-align: center; margin-bottom: 6px; }
  .photo-section {
    text-align: center; margin-bottom: 8px; height: 60px;
    display: flex; align-items: center; justify-content: center;
    font-size: 10px; color: #888;
  }
  .visitor-type {
    background: #16a34a; color: #fff; text-align: center;
    font-weight: bold; font-size: 12px; padding: 4px 0;
    margin-bottom: 10px; letter-spacing: 1px; text-transform: uppercase;
  }
  .details-section { margin-bottom: 10px; overflow: hidden; }
  .detail-row { display: flex; font-size: 9px; margin-bottom: 3px; line-height: 1.2; }
  .label { width: 65px; text-align: left; margin-right: 4px; color: #000; }
  .value { flex: 1; color: #000; }
  .signature-section { position: absolute; bottom: 12px; left: 12px; font-size: 9px; color: #000; }
  .qr-placeholder {
    position: absolute; bottom: 12px; right: 12px;
    width: 60px; height: 60px;
    display: flex; align-items: center; justify-content: center;
    font-size: 14px; color: #000;
  }
</style>
</head>
<body>
<div class="badge-container">
<div class="logo-section">#Logo#</div>
<div class="photo-section">#Photo#</div>
<div class="visitor-type">VISITOR PASS</div>
<div class="details-section">
  <div class="detail-row"><div class="label">PassId:</div><div class="value">#PassId#</div></div>
  <div class="detail-row"><div class="label">Name:</div><div class="value">#Name#</div></div>
  <div class="detail-row"><div class="label">Org:</div><div class="value">#Org#</div></div>
  <div class="detail-row"><div class="label">To Meet:</div><div class="value">#To Meet#</div></div>
  <div class="detail-row"><div class="label">Purpose:</div><div class="value">#Purpose#</div></div>
  <div class="detail-row"><div class="label">Location:</div><div class="value">#Location#</div></div>
  <div class="detail-row"><div class="label">Gates:</div><div class="value">#Gates#</div></div>
  <div class="detail-row"><div class="label">Entity:</div><div class="value">#Buildings#</div></div>
  <div class="detail-row"><div class="label">IN-Time:</div><div class="value">#IN-Time#</div></div>
  <div class="detail-row"><div class="label">ValidTill:</div><div class="value">#ValidTill#</div></div>
  <div class="detail-row"><div class="label">Assets:</div><div class="value">#Assets#</div></div>
  <div class="detail-row"><div class="label">Remarks:</div><div class="value">#Remarks#</div></div>
  <div class="detail-row" style="margin-top:10px;"><div class="label">Visitor Sign</div></div>
</div>
<div class="signature-section">Employee Sign</div>
<div class="qr-placeholder">#QR#</div>
</div>
</body>
</html>`;

const BACK_SEPARATOR = '<!-- HTML Back: -->';

const parseCombinedHtml = (htmlFront = '') => {
  const idx = htmlFront.indexOf(BACK_SEPARATOR);
  if (idx === -1) return { frontHtml: htmlFront, backHtml: '' };
  return {
    frontHtml: htmlFront.slice(0, idx).trim(),
    backHtml:  htmlFront.slice(idx + BACK_SEPARATOR.length).trim(),
  };
};

const extractQuillContent = (htmlStr) => {
  if (!htmlStr) return '';
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlStr, 'text/html');

    // FIX: Only convert non-hr elements that have inline border-top styles.
    // This handles the OLD original DB format (div with border-top style).
    // Do NOT process actual <hr> tags here — they are handled in the next step.
    const borderElements = Array.from(doc.querySelectorAll('*')).filter(el => {
      return (
        el.tagName !== 'HR' &&                          // ← skip real <hr> tags
        el.style &&
        el.style.borderTop &&
        el.style.borderTop.includes('solid')
      );
    });
    borderElements.forEach(el => {
      el.style.borderTop = '';
      const p = doc.createElement('p');
      p.innerText = DIVIDER_TEXT;
      el.parentNode.insertBefore(p, el);
    });

    // Convert all <hr> tags (and any legacy p.divider) into divider placeholders
    const dividers = doc.querySelectorAll('p.divider, p[data-divider="true"], hr');
    dividers.forEach(node => {
      const p = doc.createElement('p');
      p.innerText = DIVIDER_TEXT;
      node.parentNode.replaceChild(p, node);
    });

    // 1. Try .content-section first
    const contentSection = doc.querySelector('.content-section');
    if (contentSection) return contentSection.innerHTML.trim();

    // 2. Fallback: .badge-container minus logo divs
    const badgeContainer = doc.querySelector('.badge-container');
    if (badgeContainer) {
      const logo = badgeContainer.querySelector('.logo-section');
      if (logo) logo.remove();
      const firstDiv = badgeContainer.querySelector('div[style*="height:30px"], div[style*="height: 30px"]');
      if (firstDiv) firstDiv.remove();
      return badgeContainer.innerHTML.trim();
    }

    // 3. Final fallback: body
    const body = doc.querySelector('body');
    if (body) return body.innerHTML.trim();
  } catch (e) {
    console.error('Error parsing HTML:', e);
  }
  return htmlStr;
};

/* ═══════════════════════════════════════════════════════
   QUILL CONFIG
═══════════════════════════════════════════════════════ */
// Register custom fonts
const Font = Quill.import('attributors/style/font');
Font.whitelist = ['sans-serif', 'serif', 'monospace', 'arial', 'times-new-roman', 'courier-new'];
Quill.register(Font, true);

// Register custom sizes (in px)
const Size = Quill.import('attributors/style/size');
Size.whitelist = ['8px', '9px', '10px', '11px', '12px', '14px', '16px', '18px', '20px', '24px'];
Quill.register(Size, true);

const QUILL_MODULES = {
  toolbar: [
    [{ font: ['sans-serif', 'serif', 'monospace', 'arial', 'times-new-roman', 'courier-new'] }],
    [{ size: ['8px', '9px', '10px', '11px', '12px', '14px', '16px', '18px', '20px', '24px'] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['clean'],
  ],
};

const QUILL_FORMATS = ['font', 'size', 'bold', 'italic', 'underline', 'list', 'align'];

const stripHtml = (html = '') => html.replace(/<[^>]*>/g, '').trim();

/* ═══════════════════════════════════════════════════════
   RESPONSIVE IFRAME
═══════════════════════════════════════════════════════ */
const ScaledBadgeIframe = ({ srcDoc, title, nativeW = BADGE_NATIVE_W, nativeH = BADGE_NATIVE_H }) => {
  const wrapRef = useRef(null);
  const [zoom,  setZoom]  = useState(1);
  const [wrapH, setWrapH] = useState(nativeH);

  const recalc = useCallback(() => {
    if (!wrapRef.current) return;
    const availW = wrapRef.current.clientWidth;
    const z      = availW / nativeW;
    setZoom(z);
    setWrapH(Math.round(nativeH * z));
  }, [nativeW, nativeH]);

  useEffect(() => {
    recalc();
    const ro = new ResizeObserver(recalc);
    if (wrapRef.current) ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, [recalc]);

  const srcKey = srcDoc ? srcDoc.length : 0;

  return (
    <div
      ref={wrapRef}
      style={{
        width: '100%',
        height: wrapH,
        overflow: 'hidden',
        background: '#fff',
        borderRadius: 6,
      }}
    >
      <iframe
        key={srcKey}
        title={title}
        srcDoc={srcDoc}
        scrolling="no"
        sandbox="allow-same-origin"
        style={{
          border: 'none',
          width:  nativeW,
          height: nativeH,
          display: 'block',
          pointerEvents: 'none',
          overflow: 'hidden',
          zoom: zoom,
        }}
      />
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   STYLED COMPONENTS  (unchanged from original)
═══════════════════════════════════════════════════════ */
const PageWrapper = styled.div`
  padding: 24px 28px;
  background: #f7f8fc;
  min-height: 100vh;
  font-family: 'Segoe UI', sans-serif;
`;
const PageTitle = styled.h1`
  font-size: 24px;
  font-family: "Segoe UI", "Semibold";
  color: #2b3497;
`;
const FilterCard = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 18px 22px;
  margin-bottom: 22px;
`;
const FilterLabel = styled.label`
  display: block;
  font-size: 16px;
  font-weight: 600;
  color: #000000;
  margin-bottom: 6px;
`;
const StyledSelect = styled.select`
  border: 1px solid #2b3497;
  border-radius: 6px;
  padding: 9px 36px 9px 12px;
  font-size: 14px;
  color: #1a1f36;
  min-width: 220px;
  background: #fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E") no-repeat right 12px center;
  appearance: none;
  cursor: pointer;
  outline: none;
  &:focus { border-color: #2b3497; box-shadow: 0 0 0 2px rgba(43,52,151,0.1); }
`;
const ContentRow = styled.div`
  display: flex;
  gap: 18px;
  align-items: flex-start;
  flex-wrap: wrap;
`;
const PreviewSection = styled.div`
  flex: 1;
  min-width: 0;
`;
const PreviewHeading = styled.h2`
  font-size: 16px;
  font-weight: 700;
  color: #1a1f36;
  margin: 0 0 14px;
`;
const CardsRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: stretch;
  margin-left: 10px;
  flex-wrap: wrap;
`;
const PassCard = styled.div`
  flex: 1;
  min-width: 280px;
  max-width: 420px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;
const PassCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 11px 14px;
  border-bottom: 1px solid #e5e7eb;
  background: ${({ editable }) => editable ? '#f0fdf4' : '#f0f4ff'};
  flex-shrink: 0;
  svg { color: ${({ editable }) => editable ? '#16a34a' : '#2b3497'}; }
`;
const PassCardTitle = styled.span`
  font-size: 18px;
  font-weight: 600;
  color: ${({ editable }) => editable ? '#16a34a' : '#2b3497'};
`;
const PassCardTag = styled.span`
  font-size: 11px;
  color: #9ca3af;
  margin-left: 2px;
`;
const PassCardBody = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
`;
const FrontPassNote = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #4b5563;
  margin-top: 10px;

  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }
`;
const EditBackPassBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  border: 1.5px solid #2b3497;
  color: #2b3497;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 12px;
  white-space: nowrap;
  transition: all 0.15s;
  &:hover { background: #2b3497; color: #fff; }
`;
const SidePanel = styled.div`
  width: 720px;
  flex-shrink: 0;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 22px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  position: sticky;
  top: 10px;
  margin-top: 34px;
  max-height: calc(100vh - 40px);
  overflow-y: auto;
  transform-origin: top right;
  animation: panelSlideIn 0.25s ease forwards;
  @keyframes panelSlideIn {
    from { opacity: 0; transform: translateX(30px) scale(0.97); }
    to   { opacity: 1; transform: translateX(0) scale(1); }
  }
`;
const SidePanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-shrink: 0;
`;
const SidePanelTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #1a1f36;
  margin: 0 0 3px;
`;
const SidePanelSubtitle = styled.p`
  font-size: 12.5px;
  color: #6b7280;
  margin: 0;
`;
const CloseBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  padding: 3px;
  border-radius: 4px;
  flex-shrink: 0;
  margin-top: 2px;
  &:hover { color: #1a1f36; background: #f3f4f6; }
`;
const EditorLabel = styled.div`
  font-size: 12.5px;
  font-weight: 700;
  color: #1a1f36;
  margin-bottom: 6px;
  span { font-weight: 400; color: #6b7280; font-size: 11px; margin-left: 4px; }
`;
const QuillWrapper = styled.div`
  .ql-toolbar { border-radius: 6px 6px 0 0; border-color: #e5e7eb; padding: 3px 6px; background: #fff; position: sticky; top: 0; z-index: 1; }
  .ql-container { border-radius: 0 0 6px 6px; border-color: #e5e7eb; font-size: 12.5px; }
  .ql-editor { min-height: 360px; max-height: 520px; overflow-y: auto; line-height: 1.5; }

  /* Editor rendering */
  .ql-font-arial         { font-family: Arial, sans-serif !important; }
  .ql-font-times-new-roman { font-family: 'Times New Roman', serif !important; }
  .ql-font-courier-new   { font-family: 'Courier New', monospace !important; }
  .ql-font-serif         { font-family: Georgia, serif !important; }
  .ql-font-monospace     { font-family: 'Courier New', monospace !important; }

  /* Picker label — default */
  .ql-font .ql-picker-label:not([data-value])::before,
  .ql-font .ql-picker-label[data-value=""]::before       { content: 'Font' !important; }

  /* Picker label — selected */
  .ql-font .ql-picker-label[data-value="sans-serif"]::before    { content: 'Sans-serif' !important; }
  .ql-font .ql-picker-label[data-value="serif"]::before         { content: 'Serif' !important; }
  .ql-font .ql-picker-label[data-value="monospace"]::before     { content: 'Monospace' !important; }
  .ql-font .ql-picker-label[data-value="arial"]::before         { content: 'Arial' !important; }
  .ql-font .ql-picker-label[data-value="times-new-roman"]::before { content: 'Times New' !important; }
  .ql-font .ql-picker-label[data-value="courier-new"]::before   { content: 'Courier New' !important; }

  /* Picker dropdown items */
  .ql-font .ql-picker-item[data-value="sans-serif"]::before     { content: 'Sans-serif' !important; font-family: sans-serif; }
  .ql-font .ql-picker-item[data-value="serif"]::before          { content: 'Serif' !important; font-family: Georgia, serif; }
  .ql-font .ql-picker-item[data-value="monospace"]::before      { content: 'Monospace' !important; font-family: 'Courier New', monospace; }
  .ql-font .ql-picker-item[data-value="arial"]::before          { content: 'Arial' !important; font-family: Arial, sans-serif; }
  .ql-font .ql-picker-item[data-value="times-new-roman"]::before { content: 'Times New' !important; font-family: 'Times New Roman', serif; }
  .ql-font .ql-picker-item[data-value="courier-new"]::before    { content: 'Courier New' !important; font-family: 'Courier New', monospace; }


    /* Fix size picker items showing actual px values */
  .ql-size .ql-picker-label::before { content: 'Size' !important; }
  .ql-size .ql-picker-item[data-value="8px"]::before  { content: '8px'  !important; font-size: 8px;  }
  .ql-size .ql-picker-item[data-value="9px"]::before  { content: '9px'  !important; font-size: 9px;  }
  .ql-size .ql-picker-item[data-value="10px"]::before { content: '10px' !important; font-size: 10px; }
  .ql-size .ql-picker-item[data-value="11px"]::before { content: '11px' !important; font-size: 11px; }
  .ql-size .ql-picker-item[data-value="12px"]::before { content: '12px' !important; font-size: 12px; }
  .ql-size .ql-picker-item[data-value="14px"]::before { content: '14px' !important; font-size: 14px; }
  .ql-size .ql-picker-item[data-value="16px"]::before { content: '16px' !important; font-size: 16px; }
  .ql-size .ql-picker-item[data-value="18px"]::before { content: '18px' !important; font-size: 18px; }
  .ql-size .ql-picker-item[data-value="20px"]::before { content: '20px' !important; font-size: 20px; }
  .ql-size .ql-picker-item[data-value="24px"]::before { content: '24px' !important; font-size: 24px; }

  /* Size picker label display */
  .ql-size .ql-picker-label::before { content: 'Size'; }
  .ql-size .ql-picker-item::before { content: attr(data-value); }
`;
const CharCount = styled.div`
  font-size: 11px;
  color: ${({ overlimit }) => overlimit ? '#dc2626' : '#9ca3af'};
  font-weight: ${({ overlimit }) => overlimit ? '600' : '400'};
  text-align: right;
  margin-top: 3px;
`;
const SidePanelFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 10px;
  border-top: 1px solid #f3f4f6;
  flex-shrink: 0;
`;
const BtnRestore = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  background: #fff;
  border: 1.5px solid #d1d5db;
  color: #374151;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;
  &:hover { border-color: #2b3497; color: #2b3497; }
`;
const BtnSave = styled.button`
  background: #2b3497;
  border: none;
  color: #fff;
  border-radius: 6px;
  padding: 8px 18px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &:not(:disabled):hover { background: #1e2570; }
`;

/* ═══════════════════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════════════════ */
const BadgeTemplatePreviewPage = () => {
  const [templates,          setTemplates]          = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedTemplate,   setSelectedTemplate]   = useState(null);
  const [frontPassHtml,      setFrontPassHtml]      = useState(FRONT_PASS_HTML);
  const [showPanel,          setShowPanel]          = useState(false);
  const [saving,             setSaving]             = useState(false);
  const [backContent,        setBackContent]        = useState('');

  const backStorageMode = useRef('combined'); // 'combined' | 'separate'

  // Stores the clean original front-only HTML string (no back, no style patches)
  const rawOriginalFrontOnly = useRef('');
  const quillRef = useRef(null);
  const [charCount, setCharCount] = useState(0);

  // Preview always shows right-side-up (no rotation) using the preview builder
  const previewHtml = buildBackPassPreviewHtml(backContent);

  const defaultContent = useRef('');

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const tempResponse = await axios.get('/BadgeTemplateMap/GetBadgeTemplate');
        setTemplates(tempResponse.data ?? []);
      } catch (err) {
        console.error(err);
        toast.error('Failed to load templates.');
      }
    };
    fetchTemplates();
  }, []);

  useEffect(() => {
  if (!showPanel) return;

  // Wait for Quill to mount
  const timer = setTimeout(() => {
    if (!quillRef.current) return;
    const editor = quillRef.current.getEditor();

    // Block keyboard input when at/over limit
    editor.keyboard.addBinding(
      { key: /[\s\S]/, shiftKey: null },
      { collapsed: true },
      function (range) {
        const count = Math.max(0, editor.getText().length - 1);
        if (count >= MAX_CHARS) return false; // block the keystroke
        return true;
      }
    );

    // Handle paste: trim pasted text to fit within remaining chars
    editor.clipboard.addMatcher(Node.TEXT_NODE, (node, delta) => {
      const editor2 = quillRef.current?.getEditor();
      if (!editor2) return delta;
      const currentCount = Math.max(0, editor2.getText().length - 1);
      const remaining = MAX_CHARS - currentCount;
      if (remaining <= 0) return { ops: [] }; // block entire paste

      // Trim each text op to fit within remaining chars
      let used = 0;
      const trimmedOps = delta.ops.map((op) => {
        if (typeof op.insert === 'string') {
          const available = Math.max(0, remaining - used);
          const trimmed = op.insert.slice(0, available);
          used += trimmed.length;
          return { ...op, insert: trimmed };
        }
        return op;
      });
      return { ops: trimmedOps };
    });
  }, 100);

  return () => clearTimeout(timer);
}, [showPanel]);

const getCharCount = useCallback(() => {
  if (quillRef.current) {
    const editor = quillRef.current.getEditor();
    const text = editor.getText();
    // Quill always appends a trailing '\n', so subtract 1
    return Math.max(0, text.length - 1);
  }
  return stripHtml(backContent).length;
}, [backContent]);

  const buildFrontPassPreviewHtml = (dbFrontHtml) => {
  // FRONT_PASS_HTML is already 265×378px with correct layout.
  // We just detect the visitor-type color from the DB HTML and apply it,
  // since different templates (VISITOR=green, CONTRACTOR=grey, VIP=gold etc.)
  // may use different colors.
  let visitorTypeBg = '#16a34a'; // default green

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(dbFrontHtml, 'text/html');
    const vtEl = doc.querySelector('.visitor-type');
    if (vtEl && vtEl.style.background) {
      visitorTypeBg = vtEl.style.background;
    } else if (vtEl) {
      // Try computed background from class style in <style> tag
      const styleContent = doc.querySelector('style')?.textContent || '';
      const match = styleContent.match(/\.visitor-type\s*\{[^}]*background:\s*([^;!]+)/);
      if (match) visitorTypeBg = match[1].trim();
    }

    // Also extract the visitor label text (VISITOR PASS / CONTRACTOR PASS etc.)
    const vtText = vtEl?.textContent?.trim() || 'VISITOR PASS';

    // Inject the correct color and label into FRONT_PASS_HTML
    return FRONT_PASS_HTML
      .replace(
        'background: #16a34a',
        `background: ${visitorTypeBg}`
      )
      .replace(
        '>VISITOR PASS<',
        `>${vtText}<`
      );
  } catch (e) {
    console.error('buildFrontPassPreviewHtml error:', e);
  }

  return FRONT_PASS_HTML;
};

  const handleTemplateChange = (e) => {
  const id = e.target.value;
  if (!id) { setSelectedTemplateId(''); setSelectedTemplate(null); return; }

  const tpl = templates.find((t) => String(t.id) === String(id));
  if (!tpl) return;

  setSelectedTemplateId(id);
  setShowPanel(false);

  const hasCombined = tpl.htmlFront && tpl.htmlFront.includes(BACK_SEPARATOR);

  let frontHtml = '';
  let backHtml  = '';

  if (hasCombined) {
    backStorageMode.current = 'combined';
    const parsed = parseCombinedHtml(tpl.htmlFront);
    frontHtml = parsed.frontHtml;
    backHtml  = parsed.backHtml;
    rawOriginalFrontOnly.current = frontHtml;
  } else {
    backStorageMode.current = 'separate';
    frontHtml = tpl.htmlFront || '';
    backHtml  = tpl.htmlBack  || '';
    rawOriginalFrontOnly.current = '';
  }

  setSelectedTemplate(tpl);

  // ── Front pass preview ───────────────────────────────────────────────────
  // The DB HTML is A4-sized (21cm wide) — unsuitable for a 265px iframe.
  // Instead of patching it, we use FRONT_PASS_HTML (already 265×378px)
  // and copy over the visitor-type color/label from the DB template.
  // All #Placeholder# tokens remain as-is for the preview.
  // ────────────────────────────────────────────────────────────────────────
  const previewFrontHtml = buildFrontPassPreviewHtml(frontHtml);
  setFrontPassHtml(previewFrontHtml);

  const initialBack = extractQuillContent(backHtml);
  setBackContent(initialBack);
  defaultContent.current = initialBack;
  syncCharCount();
};

const MAX_CHARS = 2000;

const handleEditorChange = (val) => {
  if (quillRef.current) {
    const editor = quillRef.current.getEditor();
    const count = Math.max(0, editor.getText().length - 1);
    setCharCount(count);
    setBackContent(editor.root.innerHTML);
  } else {
    setCharCount(stripHtml(val).length);
    setBackContent(val);
  }
};

const syncCharCount = useCallback(() => {
  setTimeout(() => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      const count = Math.max(0, editor.getText().length - 1);
      setCharCount(count);
    }
  }, 50);
}, []);

const getAccurateCharCount = () => {
  if (quillRef.current) {
    const editor = quillRef.current.getEditor();
    // getText() appends a trailing '\n', subtract 1
    return Math.max(0, editor.getText().length - 1);
  }
  return stripHtml(backContent).length;
};

  const handleRestoreDefault = () => {
  setBackContent(defaultContent.current);
  syncCharCount(); // ← add this
  };

  const handleOpenPanel = () => setShowPanel(true);

  /* ── Save Changes ──────────────────────────────────────────────────────────
     Key rules:
     • 'combined' mode → use buildBackPassForStorage_Combined (A4 page structure
       with .back-rotate) to match the original DB format exactly. Reassemble as:
         <original front HTML> \n BACK_SEPARATOR \n <new back HTML>
     • 'separate' mode → use buildBackPassForStorage_Separate (standalone badge
       doc with rotate on .badge-container). htmlFront unchanged, htmlBack updated.
  ────────────────────────────────────────────────────────────────────────── */
  const handleSaveChanges = async () => {
    setSaving(true);

    try {
      let htmlFrontPayload = null;
      let htmlBackPayload  = null;

      if (backStorageMode.current === 'combined') {
        // Build the back HTML in the same A4-page format the print engine expects
        const newBackHtml = buildBackPassForStorage_Combined(backContent);

        // Use the stored original front-only portion (before any preview style patches)
        // to avoid accumulating extra <style> blocks on repeated saves.
        const frontOnly = rawOriginalFrontOnly.current;

        htmlFrontPayload = `${frontOnly}\n${BACK_SEPARATOR}\n${newBackHtml}`;
        htmlBackPayload  = null;

        console.log('Combined payload front-only length:', frontOnly.length);
        console.log('Combined payload back HTML length:', newBackHtml.length);

      } else {
        // 'separate' mode: only update htmlBack; leave htmlFront untouched
        const newBackHtml = buildBackPassForStorage_Separate(backContent);

        htmlFrontPayload = selectedTemplate.htmlFront;
        htmlBackPayload  = newBackHtml;
      }

      const response = await axios.post('/BadgeTemplateMap/UpdateBadgeTemplate', {
        id:          selectedTemplate.id,
        name:        null,
        photo:       null,
        isActive:    selectedTemplate.isActive ?? true,
        createdOn:   selectedTemplate.createdOn,
        createdBy:   null,
        modifiedOn:  null,
        modifiedBy:  null,
        htmlFront:   htmlFrontPayload,
        htmlBack:    htmlBackPayload,
        htmlBaggage: null,
      });

      if (response.data.message && response.status === 200 && response.data.code === 0) {
        toast.success(response.data.message);

        // ── Update local state so re-opening the editor reflects saved content ──
        // Reconstruct what the template now looks like in the DB so that
        // a second "Edit" open doesn't revert to stale data.
        if (backStorageMode.current === 'combined') {
          setSelectedTemplate(prev => ({
            ...prev,
            htmlFront: htmlFrontPayload,
            htmlBack:  null,
          }));
        } else {
          setSelectedTemplate(prev => ({
            ...prev,
            htmlBack: htmlBackPayload,
          }));
        }
      }

      setShowPanel(false);

    } catch (err) {
      console.error(err);
      toast.error('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageWrapper>
      <PageTitle>Badge Template</PageTitle>

      <FilterCard>
        <FilterLabel>Badge Template</FilterLabel>
        <StyledSelect value={selectedTemplateId} onChange={handleTemplateChange}>
          <option value="">-- Select Template --</option>
          {templates.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </StyledSelect>
      </FilterCard>

      {selectedTemplate && (
        <ContentRow>
          <PreviewSection>
            <PreviewHeading>Badge Preview</PreviewHeading>
            <CardsRow>

              {/* ── Front Pass ── */}
              <PassCard>
                <PassCardHeader>
                  <FiLock size={18} />
                  <PassCardTitle>Front Pass</PassCardTitle>
                  <PassCardTag>(Read Only)</PassCardTag>
                </PassCardHeader>
                <PassCardBody>
                  <ScaledBadgeIframe
                    title="front-pass-preview"
                    srcDoc={frontPassHtml}
                    nativeW={267}
                    nativeH={380}
                  />
                  <FrontPassNote>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  Front pass cannot be edited.
                </FrontPassNote>
                </PassCardBody>
              </PassCard>

              {/* ── Back Pass ── */}
              <PassCard>
                <PassCardHeader editable>
                  <FiEdit2 size={18} />
                  <PassCardTitle editable>Back Pass</PassCardTitle>
                  <PassCardTag>(Editable)</PassCardTag>
                </PassCardHeader>
                <PassCardBody>
                  <ScaledBadgeIframe
                    title="back-pass-preview"
                    srcDoc={previewHtml}
                  />
                  <EditBackPassBtn onClick={handleOpenPanel}>
                    <FiEdit size={13} />
                    Edit Back Pass
                  </EditBackPassBtn>
                </PassCardBody>
              </PassCard>

            </CardsRow>
          </PreviewSection>

          {showPanel && (
            <SidePanel>
              <SidePanelHeader>
                <div>
                  <SidePanelTitle>Edit Back Pass</SidePanelTitle>
                  <SidePanelSubtitle>
                    You can update the content of the back pass sections.
                  </SidePanelSubtitle>
                </div>
                <CloseBtn onClick={() => setShowPanel(false)}>
                  <FiX size={20} color='black'/>
                </CloseBtn>
              </SidePanelHeader>

              <div>
                <EditorLabel>
                  Back Pass Content
                  <span>(Instructions · Emergency Contact · Disclaimer · Footer)</span>
                </EditorLabel>
                <QuillWrapper onMouseDown={(e) => {
                    // Prevent toolbar clicks from stealing selection
                    if (e.target.closest('.ql-toolbar')) {
                      e.preventDefault();
                    }
                  }}>
                  <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={backContent}
                    onChange={handleEditorChange}
                    modules={QUILL_MODULES}
                    formats={QUILL_FORMATS}
                  />
                </QuillWrapper>
                <CharCount overlimit={charCount >= MAX_CHARS}>
                    {charCount} / {MAX_CHARS} characters
                    {charCount >= MAX_CHARS && ' — limit reached'}
                </CharCount>
              </div>

              <SidePanelFooter>
                <BtnRestore onClick={handleRestoreDefault}>
                  <MdOutlineRotateLeft size={14} />
                  Revert
                </BtnRestore>
                <BtnSave 
                  onClick={handleSaveChanges}
                  disabled={saving || charCount > MAX_CHARS}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </BtnSave>
              </SidePanelFooter>
            </SidePanel>
          )}
        </ContentRow>
      )}

      <ToastContainer position="top-right" autoClose={3000} />
    </PageWrapper>
  );
};

export default BadgeTemplatePreviewPage;